import React, { useEffect, useState } from 'react';
import { useTheme } from '../ThemeContext';
import { supabase } from '../supabaseClient';
import { homeownerRecords } from '../utils/homeownerApi';
import { readPushPermission } from '../utils/pushNotifications';

const DENIED_NOTE = 'Notifications are off for Pacarion. Open the iPhone Settings app, tap Pacarion, then turn Notifications on.';

function usePushPermission() {
  const [permission, setPermission] = useState('web');
  useEffect(() => {
    let cancelled = false;
    const load = () => {
      readPushPermission().then((value) => {
        if (!cancelled) setPermission(value || 'web');
      }).catch(() => {
        if (!cancelled) setPermission('web');
      });
    };
    load();
    document.addEventListener('visibilitychange', load);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', load);
    };
  }, []);
  return permission;
}

const ON = '#34c759';

function Switch({ checked, disabled, onChange, label }) {
  return (
    <button
      type="button"
      role="switch"
      className="account-push-switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={disabled ? undefined : () => onChange(!checked)}
      style={{
        width: '51px',
        height: '31px',
        borderRadius: '31px',
        border: 'none',
        padding: 0,
        background: checked ? ON : '#e5e5ea',
        position: 'relative',
        cursor: disabled ? 'wait' : 'pointer',
        flexShrink: 0,
      }}
    >
      <span
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: '2px',
          left: checked ? '22px' : '2px',
          width: '27px',
          height: '27px',
          borderRadius: '50%',
          background: '#fff',
          boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
        }}
      />
    </button>
  );
}

export function AccountPushSwitch({ enabled, busy, onChange, permission = 'web' }) {
  const { colors } = useTheme();
  const denied = permission === 'denied';
  const shown = denied ? false : enabled;
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '1rem',
      padding: '0.85rem 1.25rem',
      minHeight: '56px',
    }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: '1rem', fontWeight: 500, color: colors.text }}>Notifications</div>
        <p style={{ margin: '0.15rem 0 0', color: colors.textSecondary, fontSize: '0.85rem' }}>
          {denied ? DENIED_NOTE : 'Alerts on this phone.'}
        </p>
      </div>
      <Switch checked={shown} disabled={busy || denied} onChange={onChange} label="Notifications" />
    </div>
  );
}

export function HomeownerPushSetting() {
  const permission = usePushPermission();
  const [enabled, setEnabled] = useState(true);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    let cancelled = false;
    homeownerRecords({ resource: 'notification_preferences', action: 'get' })
      .then((result) => {
        if (cancelled) return;
        const row = result && result.data;
        setEnabled(!row || row.push_enabled !== false);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const change = async (next) => {
    const previous = enabled;
    setEnabled(next);
    setBusy(true);
    try {
      await homeownerRecords({
        resource: 'notification_preferences',
        action: 'upsert',
        record: { push_enabled: next },
      });
    } catch (saveError) {
      setEnabled(previous);
      void saveError;
    } finally {
      setBusy(false);
    }
  };

  return <AccountPushSwitch enabled={enabled} busy={busy} onChange={change} permission={permission} />;
}

export function AgentPushSetting({ profile }) {
  const permission = usePushPermission();
  const [enabled, setEnabled] = useState(true);
  const [busy, setBusy] = useState(true);
  const [rowId, setRowId] = useState('');

  useEffect(() => {
    let cancelled = false;
    if (!profile || !profile.id) {
      setBusy(false);
      return undefined;
    }
    supabase
      .from('email_preferences')
      .select('id, push_enabled')
      .eq('agent_id', profile.id)
      .maybeSingle()
      .then(({ data }) => {
        if (cancelled) return;
        if (data && data.id) setRowId(data.id);
        setEnabled(!data || data.push_enabled !== false);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [profile]);

  const change = async (next) => {
    if (!profile || !profile.id) return;
    const previous = enabled;
    setEnabled(next);
    setBusy(true);
    const payload = { push_enabled: next, updated_at: new Date().toISOString() };
    try {
      const write = rowId
        ? supabase.from('email_preferences').update(payload).eq('agent_id', profile.id)
        : supabase.from('email_preferences').insert([{ ...payload, agent_id: profile.id }]);
      const { error } = await write;
      if (error) throw error;
    } catch (saveError) {
      setEnabled(previous);
      void saveError;
    } finally {
      setBusy(false);
    }
  };

  return <AccountPushSwitch enabled={enabled} busy={busy} onChange={change} permission={permission} />;
}
